import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Check, Zap, Shield, Rocket, Cpu, Layers, Users, Play } from 'lucide-react';
import PredictorDashboardMock from '../components/site/PredictorDashboardMock';
import RoiCalculator from '../components/site/RoiCalculator';
import VideoDemoModal from '../components/site/VideoDemoModal';
import useDocumentMeta from '../hooks/useDocumentMeta';

const LOGO_URL = '/assets/evolvebpm-logo.png';

const pillars = [
  {
    icon: Sparkles,
    tag: 'PILLAR 01',
    title: 'AI-Powered Predictive Analytics',
    desc: 'Surface in-market accounts before they raise their hand. Multi-signal ranking across intent, hiring, funding, and technographic moves.',
    accent: '#4ECDC4',
  },
  {
    icon: Users,
    tag: 'PILLAR 02',
    title: 'Account-Based Targeting',
    desc: 'Precision ICP fit at account, contact, and buying-committee level. Native LinkedIn resolution + real-time deliverability checks.',
    accent: '#FFB020',
  },
  {
    icon: Layers,
    tag: 'PILLAR 03',
    title: 'White-Label Ready',
    desc: 'Deploy as your brand. Your colours, your domain, your customers. Custom themes, SSO, and enterprise access controls.',
    accent: '#4ECDC4',
  },
];

const features = [
  { icon: Cpu, title: 'Streaming inference', desc: '~1.8s / lead. Signals ingested, ranked, and routed in real time — no batch delays.' },
  { icon: Shield, title: 'Enterprise security', desc: 'SOC 2 aligned. End-to-end encryption, RBAC, audit logs, and branded folder isolation per client.' },
  { icon: Rocket, title: 'Deploy in days', desc: 'Turnkey setup. Point Predictor at your ICP + CRM, get scored accounts in your inbox within 5 days.' },
  { icon: Zap, title: '360° integrations', desc: 'Salesforce, HubSpot, Monday, LinkedIn, Bombora, 6sense — data flows in and out via native connectors + API.' },
];

const plans = [
  {
    name: 'Starter',
    price: 'Custom',
    audience: '1–2 growth teams',
    highlights: [
      'Up to 5K scored accounts / month',
      'Standard signals (intent, tech, growth)',
      'Salesforce + HubSpot connectors',
      'Slack + email digests',
    ],
    accent: '#4ECDC4',
    featured: false,
  },
  {
    name: 'Growth',
    price: 'Custom',
    audience: 'Growth + Sales aligned',
    highlights: [
      'Up to 25K scored accounts / month',
      'Full 15+ signal library',
      'Buying-committee resolution',
      'White-label branding',
      'Weekly QBRs with a strategist',
    ],
    accent: '#FFB020',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    audience: 'Global demand teams',
    highlights: [
      'Unlimited scored accounts',
      'Custom signal training',
      'Dedicated environment + SSO',
      'Multi-brand white-label',
      'Named CSM + priority SLAs',
    ],
    accent: '#4ECDC4',
    featured: false,
  },
];

const testimonials = [
  {
    quote: 'Predictor turned a spray-and-pray program into a priority list. Our SDRs now start every morning knowing exactly which 20 accounts are hottest.',
    name: 'VP Demand Gen',
    role: 'US-based IT services · Fortune 500',
  },
  {
    quote: 'The white-label deployment let us launch a client-branded ABM product in 6 weeks. Zero engineering lift on our side.',
    name: 'Founder',
    role: 'B2B Marketing Agency · London',
  },
];

export default function PredictorPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.evolvebpm.com';

  useDocumentMeta({
    title: 'Predictor.com — AI-driven, white-label ABM platform | EvolveBPM',
    description: 'Predictor.com ranks 46M contacts against your ICP + real-time buying signals in under 2 seconds. 3.1× MQL→SQL uplift, white-label ready, deploy in days.',
    canonical: `${origin}/predictor`,
  });

  React.useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('in');
        });
      },
      { threshold: 0.08 }
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#050A1F] text-[#F1EDE4]">
      {/* Slim top bar */}
      <header className="sticky top-0 z-40 bg-[#050A1F]/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="bg-white rounded-md px-2 py-1">
              <img src={LOGO_URL} alt="EvolveBPM" className="h-6 w-auto" />
            </div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-white/50">/ predictor</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden md:inline text-[13px] text-white/70 hover:text-[#4ECDC4]">
              ← Back to EvolveBPM
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-1.5 bg-[#4ECDC4] text-[#0F1C40] pl-4 pr-3 py-2 rounded-full text-[13px] font-semibold hover:bg-[#FFB020] transition-colors"
            >
              Book a demo <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute top-0 left-1/3 w-[700px] h-[700px] rounded-full opacity-40 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.35), transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.35), transparent 70%)' }}
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-32 pb-16">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#FFB020] mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Flagship product · by EvolveBPM</span>
              </div>
              <h1 className="text-6xl sm:text-7xl lg:text-8xl xl:text-9xl tracking-tight leading-[0.92] font-medium">
                <span className="font-serif italic text-[#4ECDC4]">Predictor</span>
                <span className="text-white/40">.com</span>
              </h1>
              <p className="mt-6 text-xl lg:text-2xl font-serif italic text-white/80 max-w-2xl leading-tight">
                White-labelled, AI-driven ABM.<br />
                Predict. Prioritize. Convert.
              </p>
            </div>
            <div className="lg:col-span-4 space-y-4">
              <p className="text-white/60 text-[15px] leading-relaxed">
                A production-grade ABM platform engineered for enterprise demand teams. Ranks 46M contacts against your ICP + real-time buying signals in under two seconds.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-2 bg-[#4ECDC4] text-[#0F1C40] pl-5 pr-4 py-3 rounded-full text-sm font-semibold hover:bg-[#FFB020] transition-colors"
                >
                  Book a walkthrough
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  data-testid="predictor-watch-demo-button"
                  className="inline-flex items-center gap-2 border border-white/20 pl-5 pr-4 py-3 rounded-full text-sm hover:border-[#FFB020] hover:text-[#FFB020] transition-colors group"
                >
                  <span className="w-5 h-5 rounded-full bg-[#FFB020]/20 border border-[#FFB020]/40 flex items-center justify-center group-hover:bg-[#FFB020] group-hover:border-[#FFB020] transition-colors">
                    <Play className="w-2.5 h-2.5 ml-0.5 text-[#FFB020] group-hover:text-[#0F1C40]" fill="currentColor" strokeWidth={0} />
                  </span>
                  Watch 2-min demo
                </button>
                <a
                  href="#pricing"
                  className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#4ECDC4] transition-colors underline-offset-4 hover:underline pl-2"
                >
                  See pricing ↓
                </a>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden">
            {[
              { l: 'Contacts ranked', v: '46M', c: '#4ECDC4' },
              { l: 'Signals / sec', v: '128', c: '#FFB020' },
              { l: 'Inference', v: '1.8s', c: '#4ECDC4' },
              { l: 'MQL → SQL', v: '3.1×', c: '#FFB020' },
            ].map((k) => (
              <div key={k.l} className="bg-[#0A1024] p-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">{k.l}</div>
                <div className="text-4xl lg:text-5xl font-serif italic tabular" style={{ color: k.c }}>
                  {k.v}
                </div>
              </div>
            ))}
          </div>

          {/* Dashboard mockup */}
          <div className="mt-14 reveal">
            <PredictorDashboardMock />
          </div>

          {/* Video demo tile */}
          <div className="mt-6 grid lg:grid-cols-12 gap-6 reveal">
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              data-testid="predictor-video-tile"
              className="lg:col-span-8 relative aspect-[21/9] rounded-2xl overflow-hidden bg-gradient-to-br from-[#0F1C40] via-[#0A1024] to-[#050A1F] border border-white/10 group text-left"
            >
              <div
                className="absolute inset-0 opacity-60"
                style={{
                  background:
                    'radial-gradient(circle at 25% 40%, rgba(78,205,196,0.4), transparent 55%), radial-gradient(circle at 80% 60%, rgba(255,176,32,0.35), transparent 55%)',
                }}
              />
              {/* fake filmstrip / dashboard preview slats */}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center gap-3 px-8 opacity-40 group-hover:opacity-60 transition-opacity">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="w-16 h-24 rounded-md border border-white/20 bg-white/5"
                    style={{
                      transform: `translateY(${(i % 2 === 0 ? -1 : 1) * 6}px)`,
                    }}
                  />
                ))}
              </div>
              {/* dark wash */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050A1F] via-transparent to-transparent" />

              {/* content */}
              <div className="relative h-full flex items-end p-6 lg:p-8">
                <div className="flex items-end gap-4 flex-1">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#FFB020] flex items-center justify-center shadow-[0_0_40px_rgba(255,176,32,0.5)] group-hover:scale-110 transition-transform shrink-0">
                    <Play className="w-6 h-6 lg:w-8 lg:h-8 ml-1 text-[#0F1C40]" fill="#0F1C40" strokeWidth={0} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020] mb-1">
                      Product tour · 5 chapters · 2 min
                    </div>
                    <div className="text-2xl lg:text-3xl font-serif italic text-[#F1EDE4] leading-tight">
                      See Predictor turn signals into pipeline — live.
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/40 backdrop-blur-md border border-white/10 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB020] pulse-dot" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-white/80">Watch demo</span>
              </div>
            </button>

            <div className="lg:col-span-4 bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex flex-col justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-3">What you&apos;ll see</div>
                <ul className="space-y-2.5 text-[13px] text-white/70">
                  {[
                    'Signal ingest across 15+ streams',
                    'Neural ranking in real time',
                    'Hot accounts + buying committees',
                    'Auto-route to Salesforce / Slack',
                  ].map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-[#4ECDC4] mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="mt-5 inline-flex items-center justify-center gap-2 border border-[#FFB020]/50 text-[#FFB020] py-3 rounded-full text-sm font-semibold hover:bg-[#FFB020] hover:text-[#0F1C40] transition-colors"
              >
                <Play className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} /> Play the 2-min tour
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 lg:py-28 border-t border-white/10 bg-[#0A1024]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Three pillars</span>
            </div>
            <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Built on <span className="font-serif italic text-[#4ECDC4]">signal</span>, engineered for{' '}
              <span className="font-serif italic text-[#FFB020]">scale</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.tag}
                  className="bg-[#0F1C40] border border-white/10 rounded-2xl p-8 hover:border-white/25 transition-colors reveal"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-6"
                    style={{ background: `${p.accent}20`, border: `1px solid ${p.accent}55`, color: p.accent }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: p.accent }}>
                    {p.tag}
                  </div>
                  <h3 className="text-2xl font-serif italic mb-3 leading-tight">{p.title}</h3>
                  <p className="text-[14px] text-white/70 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-20 lg:py-28 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#FFB020] mb-4">
              <span className="w-6 h-px bg-[#FFB020]" />
              <span>Under the hood</span>
            </div>
            <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Enterprise-grade capabilities, <span className="font-serif italic text-[#FFB020]">day one.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#4ECDC4]/15 border border-[#4ECDC4]/30 flex items-center justify-center text-[#4ECDC4] shrink-0">
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1">{f.title}</h3>
                    <p className="text-[13px] text-white/60 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <RoiCalculator />

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-28 border-t border-white/10 bg-[#0A1024]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Pricing tiers</span>
            </div>
            <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Three ways to <span className="font-serif italic text-[#4ECDC4]">deploy.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-8 border transition-all ${
                  plan.featured
                    ? 'bg-[#0F1C40] border-[#FFB020]/50 shadow-[0_0_60px_-20px_rgba(255,176,32,0.5)]'
                    : 'bg-[#0F1C40] border-white/10'
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-6 bg-[#FFB020] text-[#0F1C40] text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded">
                    Most popular
                  </div>
                )}
                <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: plan.accent }}>
                  {plan.audience}
                </div>
                <h3 className="text-3xl font-serif italic mb-1">{plan.name}</h3>
                <div className="text-[13px] text-white/50 mb-6">Starting from</div>
                <div className="text-4xl font-medium tabular tracking-tight mb-6">{plan.price}</div>

                <ul className="space-y-2.5 mb-8">
                  {plan.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-[13px] text-white/80">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} style={{ color: plan.accent }} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/#contact"
                  className="w-full inline-flex items-center justify-center gap-2 pl-4 pr-3 py-3 rounded-full text-sm font-semibold transition-colors"
                  style={{
                    background: plan.featured ? '#FFB020' : 'transparent',
                    color: plan.featured ? '#0F1C40' : plan.accent,
                    border: plan.featured ? 'none' : `1px solid ${plan.accent}55`,
                  }}
                >
                  Talk to sales
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 lg:p-10">
                <div className="text-4xl text-[#FFB020] font-serif italic leading-none mb-4">&ldquo;</div>
                <p className="text-xl font-serif italic leading-snug mb-6">{t.quote}</p>
                <div className="pt-4 border-t border-white/10">
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-[11px] text-white/50 font-mono uppercase tracking-widest mt-1">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 border-t border-white/10 bg-gradient-to-br from-[#0F1C40] to-[#050A1F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#FFB020] mb-6">
            Ready when you are
          </div>
          <h2 className="text-5xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.95] font-medium max-w-4xl mx-auto">
            Turn every signal into <span className="font-serif italic text-[#4ECDC4]">pipeline.</span>
          </h2>
          <p className="mt-6 text-white/60 max-w-xl mx-auto">
            See Predictor.com scoring your ICP in a 30-minute walkthrough.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-[#4ECDC4] text-[#0F1C40] pl-6 pr-5 py-4 rounded-full text-sm font-semibold hover:bg-[#FFB020] transition-colors"
            >
              Book a walkthrough
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-white/20 pl-5 pr-4 py-4 rounded-full text-sm hover:border-[#4ECDC4] hover:text-[#4ECDC4] transition-colors"
            >
              Explore EvolveBPM
            </Link>
          </div>
        </div>
      </section>

      {/* Slim footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between text-[11px] text-white/40">
          <div>© 2026 EvolveBPM · predictor.com</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[#4ECDC4]">
              Privacy
            </Link>
            <Link to="/" className="hover:text-[#4ECDC4]">
              Home
            </Link>
          </div>
        </div>
      </footer>

      <VideoDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
